import { useState, useRef, useEffect, useCallback } from 'react';
import { useChat } from '../../hooks/useChat';
import { useAttachments } from '../../hooks/useAttachments';
import { USE_MOCK, API_BASE, MOCK_DB, mockDelay } from '../../utils/constants';
import { authHeader } from '../../utils/api';
import { httpErrorHandler } from '../../utils/httpErrorHandler';
import { useTour } from '@reactour/tour';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import Sidebar from './Sidebar';
import ChatPageHeader from './ChatPageHeader';
import Message from './Message';
import TypingIndicator from './TypingIndicator';
import AttachmentsBar from './AttachmentsBar';
import InputBox from './InputBox';
import Snackbar from '../common/Snackbar';
import '@/assets/styles/ChatUI.css';

import { useSocket } from "./../../providers/SocketProvider";
import { useChatProgress } from "./../../hooks/useChatProgress";
import ProgressPanel from "./../progress/ProgressPanel";

import { v4 as uuidv4 } from "uuid";

const ScenarioDescription = `
زیست‌یار یک دستیار هوشمند برای تحلیل سناریوهای کنترل آلودگی هوا است. در این نسخه، تمرکز زیست‌یار بر دو نوع تحلیل و پیشبینی است: نخست، آلاینده \u200cهای متحرک در ناوگان حمل و نقل شهر تهران است، به این صورت که اثر اجرای سناریوهای پیشنهادی را بر میزان انتشار آلاینده\u200cها نسبت به یک زمان پایه\u200cای از قبل تعیین شده تحلیل می\u200cنماید و دوم مدل پیشبینی AQI و آلاینده های متداول هوا براساس داده های سری زمانی ۱۴۰۰ تا ۱۴۰۴. این دو مدل فعلا مستقل از هم عمل می کنند.\n
مدل محاسباتی، از اطلاعات پایه ناوگان شامل نوع وسیله نقلیه، تعداد خودروها، استاندارد آلایندگی، نوع موتور یا سوخت، پیمایش سالانه و ضریب انتشار استفاده می‌کند.\n
براساس اطلاعات منتشر شده از سال ۱۳۹۶ تعداد کل وسایل نقلیه شهر تهران برابر با است با ۷,۲۲۵,۳۰۹ و انواع وسیله نقلیه قابل استفاده در سناریوهای پیشنهادی شامل سواری، تاکسی، موتورسیکلت، وانت، اتوبوس، مینی‌بوس و کامیون است.\n
استانداردهای آلایندگی قابل تشخیص شامل No یا None، Euro1، Euro2، Euro3، Euro4، Hybrid و EV هستند.\n
نوع موتور یا سوخت نیز می‌تواند بنزینی، گازسوز، دیزلی، هیبریدی یا برقی باشد.\n
فرضیات محیطی مدل شامل دمای متوسط ۲۷ درجه سانتی‌گراد، رطوبت نسبی ۴۰ درصد، ارتفاع تقریبی تهران ۱۲۰۰ متر و گوگرد پایه بنزین برابر با ۶۹ ppm است.\n
سناریو به زبان طبیعی بیان شده و زیست‌یار تاثیر اجرای سناریو را بر میزان انتشار آلاینده بر حسب «تن در روز» بیان می‌\u200cنماید.\n
برای نمونه: «اگر ۲۰ درصد تاکسی‌های یورو ۲ گازسوز حذف شوند و ۲۰۰۰ عدد تاکسی برقی اضافه شود، چه تاثیری بر روند انتشار co دارد؟»\n
برای مدل دوم هم سامانه توانایی پاسخگویی به سوالاتی مربوط به آلاینده ها در ماه و فصول آتی را دارد. مانند: «میانگین مقدار آلاینده pm2.5 در زمستان ۱۴۰۵ چقدر خواهد بود؟»\n
`;

const ChatUI = ({ user, setUser, onLogout }) => {
	const {
		messages,
		setMessages,
		currentChatIndex,		
		isTypingChatIndex,
		setIsTypingChatIndex,
		chatIds,
		visibleChatCount,
		isLoadingMoreChats,
		handleNewChat,
		handleSidebarScroll,
		fetchChatList,
		setCurrentChatIndexCallback,
		setChatIdsCallback,
		setVisibleChatCountCallback,
		firstQueries,
		setFirstQueries,
		askWeb,
		setAskWeb
	} = useChat();

	const {
		attachments,
		setAttachments,
		fileInputRef,
		openFilePicker,
		onFilesSelected,
		removeAttachment,
		clearAttachments
	} = useAttachments();

	const {
		socketId,
		isConnected,
		connectionError,
		reconnect,
	} = useSocket();

	const {
		progressItems,
		progressError,
		startTracking,
		stopTracking,
	} = useChatProgress();

	const navigate = useNavigate()

	const [input, setInput] = useState('');
	const [sidebarOpen, setSidebarOpen] = useState(true);
	const [exportMenuOpen, setExportMenuOpen] = useState(null);
	const [exportMenuStyle, setExportMenuStyle] = useState({});
	const [chatOptionsOpen, setChatOptionsOpen] = useState(false);
	const optionsRef = useRef(null);

	const [editingIndex, setEditingIndex] = useState(null);
	const [editingText, setEditingText] = useState('');
	const [savingEditIndex, setSavingEditIndex] = useState(null);

	const [suggestions, setSuggestions] = useState([]);
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [snackbar, setSnackbar] = useState({ message: "", type: "" });

	const [requestError, setRequestError] = useState(null);
  	const [isProcessing, setIsProcessing] = useState(false);

	const [isProgressExpanded, setIsProgressExpanded] = useState(false);

	const legalQuestions = [
		"منظور قانونگذار در ماده ۱۴ قانون حفاظت و بهسازی محیط‌زیست از شاکی یا مدعی خصوصی چیست؟",
		"در قانون حفاظت و بهسازی محیط‌زیست «مناطق تحت مدیریت سازمان» چه مناطقی هستند؟",
		"تفاوت بین «حفاظت درونی» و «حفاظت بیرونی» منابع ژنتیکی را با استناد به تعاریف کنوانسیون تنوع زیستی شرح دهید.",
		"پسماند چیست؟",
		"ماده ۳ قانون هوای پاک به چه اشاره میکند؟",
		"آیا مبلغ ۲۰۰ میلیون ریال کمک‌هزینه به خانواده محیط‌بان شهید، با توجه به شرایط اقتصادی، کافی و کارآمد است؟",
		"اصل پنجاهم قانون اساسی؟",
		"هدف اصلی اتحادیه بین‌المللی حفاظت از طبیعت و منابع طبیعی (IUCN) چیست و تاکید آن بر چه نوع استفاده‌ای از منابع است؟",
		"۵ اعضای کارگروه ملی طبیعت‌گردی را نام‌برده و دبیرخانه کارگروه ملی در کجا مستقر است؟",
		"مجازات عمومی برای آبیاری فضای سبز با آب شرب (در صورت وجود آب جایگزین) چیست؟",
		"اعتبارات مرتبط با قانون هوای پاک از کجا تامین می‌شود؟",
		"در حال حاضر سازمان یک دستگاه بیل مکانیکی در اختیار دارد که بلااستفاده است آیا می‌تواند اموال دولتی را بفروشد؟",
	];

	const airQualityReports = [
		"میانگین ماهانه آلاینده PM2.5 را طی سال های ۱۴۰۰ تا ۱۴۰۴ محاسبه کن و ماه های مشابه را بین سال ها مقایسه کن.",
		"کدام آلاینده بیشترین تغییر در الگوی فصلی را طی سال های مختلف تجربه کرده است؟",
		"کدام آلاینده بیشترین همبستگی را با AQI دارد؟",
		"کدام ایستگاه بیشترین تفاوت را با میانگین شهر نشان می دهد؟",
		"در روزهای ناسالم، کدام آلاینده بیشترین سهم را در افزایش AQI داشته است؟",
		"کدام فصل بیشترین حساسیت را نسبت به افزایش PM2.5 نشان می دهد؟",
		"در سال ۱۴۰۳ برای هر استان چند رکورد کیفیت هوا ثبت شده است؟ مختصات تقریبی استان را نیز نشان بده.",
		"میانگین شاخص کیفیت هوا در ایستگاه‌های استان تهران چگونه توزیع شده است؟",
		"میانگین AQI ایستگاه های تهران در فروردین ۱۴۰۳ چقدر بوده است؟ مختصات ایستگاه ها را نیز نشان بده.",
		"کدام یک از ماه‌های سال بیشترین فراوانی روزهای بسیار ناسالم را دارند؟",
		"در چه ساعاتی از شبانه‌روز، شرایط بسیار ناسالم بیشتر مشاهده می‌شود؟",
		"سهم آلاینده‌های غالب در مشاهدات ناسالم طی سال‌های مختلف چگونه تغییر کرده است؟",
	];

	const scenarioQuestions = [
		"با کاهش ۱۵ درصد خودروهای شخصی چه تغییری در آلودگی هوا صورت میگیرد؟",
		"در صورت اسقاط ۱۳۰۰۰ موتور دودزا آیا تغییر معنی داری در آلودگی هوای تهران رخ می دهد؟",
		"دکتر نظری می خواهد در یک طرح جدید ۲۰۰۰ اتوبوس فرسوده یورو ۲ را از رده خارج کند. این تغییر چند درصد آلودگی هوا را کاهش می دهد؟",
		"میخواهیم دو طرح پیشنهادی ارتقا هوای تهران را بررسی کنیم 1) ۱۰۰۰ خودرو یورو ۲ اسقاط کنیم 2)  ۲۵۰۰ موتورسیکلت بدون استاندارد. کدام یک بهتر است؟",
		"وارد کردن ۳۰۰۰ خودرو برقی چه تاثیری برا آلودگی CO دارد؟",
		"۲۵ درصد خودرو های دیزلی را اگر از رده خارج کنیم چه تاثیری بر انتشار آلودگی CO دارد؟",
		"میانگین aqi در تابستان ۱۴۰۳ چقدر است؟",
		"پیش‌بینی مقدار pm 2.5 در مهرماه ۱۴۰۵ چقدر است؟",
		"روند فصلی pm10 در سال ۱۴۰۶ چگونه خواهد بود؟",
		"مقدار میانگین CO اردیبهشت ۱۴۰۲ با دی ماه ۱۴۰۲ مقایسه کن",
		"روند بهار ۱۴۰۴ را با بهار ۱۴۰۳ مقایسه کن",
		"چند روز Aqi بالای ۱۰۵ در سال ۱۴۰۵ خواهیم داشت؟",
	];

	const webSearchQuestions = [
		"علت مرگ ریشا شیر باغ وحش مشهد چه بود؟",
		"ماجرای توله پلنگ روستای سررود شهرستان کلات چه بود و در نتیجه چه شد؟",
		"در راستای فرهنگ‌سازی حفاظت از غارها چه اقداماتی صورت گرفته است؟",
		"آخرین وضعیت رودخانه شور ملارد چه می‌باشد؟",
		"در مورد ریشا چه خبری داری که به من بدهی؟",
		"در مورد گورخر سمنان چه اطلاعاتی داری؟",
		"طرح محیط‌یار چیست؟ در کجا قرار است که برگزار شود؟",
		"نمایشگاه محیط زیست استان تهران",
		"کد خبر f532ebe را شرح بده.",
		"درباره صرفه جویی در مصرف آب چه اخباری داریم؟",
		"خبر اعلام شده در کد خبر 3c2f2f0 ناراحت کننده است یا خوشحال کننده؟ چرا؟",
		"در بازه اردیبهشت ۱۴۰۳ تا مهر ۱۴۰۳ چه اخبار مهمی وجود دارد؟",
	];

	const handleSetShowSuggestions = useCallback((newValue) => {
		setShowSuggestions(newValue)
	}, [])


	//Get user info when the compoenent mounts
	useEffect(() => {
		const token = localStorage.getItem("token");
		if (token && !user.full_name) {
			axios.get(`${API_BASE}/user/get-me`, {
				headers: { ...authHeader() }
			})
				.then(res => {
					if (res.data?.success) setUser(res.data.user);
				}).catch(err => {
					httpErrorHandler({
						err: err,
						setSnackbar: setSnackbar,
						navigate: navigate
					});
				});
		}

		fetchChatList()
		return () => clearAttachments();
	}, []);

	const bottomRef = useRef(null);
	const exportMenuRef = useRef(null);

	// useEffect(() => {
	// 	bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
	// }, [isTypingChatIndex]);
	useEffect(() => {
		if (exportMenuOpen === null) return;
		const t = setTimeout(() => {
			const menu = exportMenuRef.current;
			if (!menu) return;
			const rect = menu.getBoundingClientRect();
			const vw = window.innerWidth;
			const margin = 8;
			let style = { left: '50%', transform: 'translateX(-50%)', right: 'auto' };
			if (rect.left < margin) style = { left: `${margin}px`, transform: 'none', right: 'auto' };
			else if (rect.right > vw - margin) style = { right: `${margin}px`, left: 'auto', transform: 'none' };
			setExportMenuStyle(style);
		}, 0);
		return () => clearTimeout(t);
	}, [exportMenuOpen]);


	const setEditingIndexCallback = (index) => {
		setEditingIndex(index);
		if (index > -1) setEditingText(messages[index].content);
	};

	const getReferences = (resources) => {
		let references = [];
		if(resources[0].url) {
			
			references = resources.map((item, index) => ({
				id: index + 1,
				// sentence_indices: item.sentence_indices || [],
				title: item.title,
				url: item.url,
			}));
		} 
		else {
			references = resources.map(file => {

				const file_name = typeof file === 'string' ? file : file['document_name'];
	
				const parts = file_name.split('.');
				parts.pop();
	
				return parts.join('.');
			});
	
			references = [...new Set(references)];
		}

		
		return references;
	}

	const saveEdit = async (index) => {

		if (!isConnected || !socketId) {
			setRequestError("ارتباط با سرور برقرار نشده است، لطفا کمی صبر کنید.");
			setSnackbar({ message: "ارتباط با سرور برقرار نشده است، لطفا کمی صبر کنید.", type: "error" });

			reconnect();
			return;
		}

		const requestId = uuidv4();
		startTracking(requestId);
		
		const messageId = messages[index]?.parent_id || messages[index]?.id;
		const updated = [...messages];
		updated[index].content = editingText;
		if (updated[index + 1]?.role === 'assistant') updated.splice(index + 1, 1);

		setMessages(updated);
		// mark saving index so we can render inline typing indicator after this message
		setSavingEditIndex(index);
		setEditingIndex(null);
		setIsTypingChatIndex(currentChatIndex);

		const sessionId = chatIds[currentChatIndex];

		if(index === 0){
			setFirstQueries(prev =>
				[
					...prev.slice(0, currentChatIndex),
					editingText,
					...prev.slice(currentChatIndex + 1),
				]
			)
		}

		if (USE_MOCK) {
			MOCK_DB.store[sessionId].messages = updated;
			await mockDelay(500);
			const mockReply = { role: 'assistant', content: 'پاسخ ماک بعد از ویرایش پیام کاربر.\n*تست بولد* و خط جدید.' };
			MOCK_DB.store[sessionId].messages.push(mockReply);
			setMessages([...MOCK_DB.store[sessionId].messages]);
			setSavingEditIndex(null);
			setIsTypingChatIndex(-1);
			return;
		}

		const userId = localStorage.getItem('user_id');
		setIsProcessing(true);
		setRequestError('');

		axios.patch(`${API_BASE}/chat/edit/${messageId}`,
			{
				query: editingText,
				inputs: {
					similarity_threshold: '0.5',
					context_count: 5
				},
				response_mode: "blocking",
				user: userId,
				conversation_id: sessionId,
				files: [],
				auto_generate_name: true,
				socket_id: socketId,
				ask_web_search: askWeb
			},
			{ headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...authHeader() } })
			.then(res => {
				const data = res.data
				
				setMessages((prev) => [
					...prev.slice(0, index),

					{
						...prev[index],
						parent_id: prev[index]?.parent_id ?? messageId,
					},

					{
						id: data.id,
						role: 'assistant',
						content: data.answer,
						references: data.metadata?.retriever_resources?.length ? getReferences(data.metadata.retriever_resources) : [],
						charts: data.charts,
						report: data.report
					},
					...prev.slice(index + 1)
				]);
			})
			.catch(err => {
				httpErrorHandler({
					err: err,
					setSnackbar: setSnackbar,
					navigate: navigate,
					setRequestError: setRequestError
				});
			})
			.finally(() => {
				setIsTypingChatIndex(-1);
				setSavingEditIndex(null);
				setIsProcessing(false);
				setIsProgressExpanded(false);
			})
	};


	useEffect(() => {
		if (!chatOptionsOpen) return;

		function handleClickOutside(event) {
			if (optionsRef.current && !optionsRef.current.contains(event.target))
				setChatOptionsOpen(false)
		}


		document.addEventListener('click', handleClickOutside);

		return () => {
			document.removeEventListener('click', handleClickOutside)
		}

	}, [chatOptionsOpen])

	//feedback
	const handleFeedback = (index, action) => {

		const sessionId = chatIds[currentChatIndex];
		const messageId = messages[index]?.id;

		if(messageId === 'scenario-description'){
			return;
		}

		const currentFeedback = messages[index]?.feedback;

		let requestBody;

		if (currentFeedback === action)
			requestBody = {};
		else if (action === "liked")
			requestBody = { feedback: true };
		else if (action === "disliked")
			requestBody = { feedback: false };

		axios.patch(`${API_BASE}/chat/${sessionId}/feedback/${messageId}`,
			requestBody,
			{
				headers: { ...authHeader() }
			},)
			.then((res) => {
				if (res.data?.success)
					setMessages((prev) => {
						return prev.map((item, i) => {
							if (i === index) {
								if (requestBody.feedback === undefined)
									return { ...item, feedback: 'none' };

								if (requestBody.feedback === true)
									return { ...item, feedback: "liked" };

								if (requestBody.feedback === false)
									return { ...item, feedback: "disliked" };
							}
							return { ...item };
						}
						)
					})
			}).catch(err => {
				httpErrorHandler({
					err: err,
					setSnackbar: setSnackbar,
					navigate: navigate
				});
			})
	}

	const handleSend = async () => {
		if (!input.trim() && attachments.length === 0) return;

		if (!isConnected || !socketId) {
			setRequestError("ارتباط با سرور برقرار نشده است، لطفا کمی صبر کنید.");
			setSnackbar({ message: "ارتباط با سرور برقرار نشده است، لطفا کمی صبر کنید.", type: "error" });

			reconnect();
			return;
		}

		const requestId = uuidv4();
		startTracking(requestId);

		requestAnimationFrame(() => {
			bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
		});

		const sessionId = chatIds[currentChatIndex];
		
		const userMessage = { role: 'user', content: input.replace(/\n/g, '\n\n'), attachments };

		setMessages(prev => [...prev, userMessage]);
		setInput('');
		setAttachments([]);
		setIsTypingChatIndex(currentChatIndex);
		setIsProcessing(true);
		setRequestError('');

		if (USE_MOCK) {
			MOCK_DB.store[sessionId].messages.push(userMessage);
			await mockDelay(700);
			const filesNote = attachments.length ? `فایل‌ها دریافت شد: ${attachments.map(a => a.name).join(', ')}` : 'بدون فایل';
			const mockReply = { role: 'assistant', content: `پاسخ ماک: "${userMessage.content || '—'}"\n${filesNote}` };
			MOCK_DB.store[sessionId].messages.push(mockReply);
			setMessages([...MOCK_DB.store[sessionId].messages]);
			return;
		}

		const userId = localStorage.getItem('user_id');

		let body = null;
		const formData = new FormData();
		if (attachments.length > 0) {
			if (attachments.length === 1) {
				formData.append('file', attachments[0].file, attachments[0].name);
			} else if (attachments.length > 1) {
				attachments.forEach(a => formData.append('files', a.file, a.name));
			}

			const requestInputs = {
					similarity_threshold: "0.5",
					context_count: 5
				}

			formData.append('query', userMessage.content);
			formData.append('inputs', requestInputs);
			formData.append('response_mode', "blocking");
			formData.append('user', userId);
			formData.append('conversation_id', sessionId);
			formData.append('auto_generate_name', true);
			formData.append('ask_web_search', askWeb);

			body = formData
		} else {
			body =
			{
				query: userMessage.content,
				inputs: {
					similarity_threshold: "0.5",
					context_count: 5
				},
				response_mode: "blocking",
				user: userId,
				conversation_id: sessionId ?? '',
				files: [],
				auto_generate_name: true,
				socket_id: socketId,
				ask_web_search: askWeb
			}
		}

		axios.post(`${API_BASE}/chat/chat-messages`, body, {
			headers: {
				...authHeader()
			}
		}).then(async (res) => {
			const data = res.data;						

			const references = data.metadata?.retriever_resources?.length ? getReferences(data.metadata.retriever_resources) : []

			let assistant_msg = {
				id: data.id,
				messageId: data.message_id,
				role: 'assistant',
				content: data.answer,
				feedback: "none",
				references: references,
				sessionId: data.conversation_id
			}

			if(data?.charts){
				assistant_msg['charts'] = data.charts
			}

			if(data?.report){
				assistant_msg['report'] = data.report
			}

			// if this is the first message sent
			if (!sessionId) {	
				setChatIdsCallback(data.conversation_id);
				const index = sessionId === '' ? chatIds.length - 1 : chatIds.length
				// const existedId = chatIds.findIndex(item => item === data.conversation_id)
				// let index;
				// if(existedId >= 0){
				// 	index = existedId;
				// } else {
				// 	index = sessionId === '' ? chatIds.length - 1 : chatIds.length;
				// }
				setVisibleChatCountCallback(chatIds.length + 1);						
				setCurrentChatIndexCallback(index);
			}

			setMessages(prev => {
				if (prev.length === 0) return prev;

				const userMessage = {
					...prev[prev.length - 1],
					id: data.id,
					messageId: data.message_id,
				};

				return [
					...prev.slice(0, -1),
					userMessage,
					assistant_msg
				];
			});
			setFirstQueries(prev => [...prev, input])

			requestAnimationFrame(() => {
			bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
		});
			
		}
		).catch((err) => {
			httpErrorHandler({
				err: err,
				setSnackbar: setSnackbar,
				navigate: navigate,
				setRequestError: setRequestError
			});
		}).finally(() => {
			setIsProcessing(false);
			setIsProgressExpanded(false);
			setIsTypingChatIndex(-1);
		})
	};

	const toggleExportMenu = (i) => {
		setExportMenuOpen((prev) => (prev === i ? null : i));
		setExportMenuStyle({});
	};

	const onDrop = (e) => {
		e.preventDefault();
		if (e.dataTransfer?.files?.length) onFilesSelected(e.dataTransfer.files);
	};

	const onDragOver = (e) => e.preventDefault();


	const { isOpen, setIsOpen } = useTour()

	const onQuestionMarkClick = () => {
		if (!sidebarOpen) setSidebarOpen(true)
		setMessages([...MOCK_DB.store['c1'].messages])
		setIsOpen(true)
	};

	useEffect(() => {
		if (!isOpen) setMessages([])
	}, [isOpen])

	const chatHeaderRef = useRef(null);
	const sidebarRef = useRef(null);

	const [mobileIsOpen, setMobileIsOpen] = useState(false);

	const handleSelectChat = async (index) => {
		if (index === currentChatIndex) return;

		const sessionId = chatIds[index];		

		if (USE_MOCK) {
			await mockDelay(200);
			setMessages([...MOCK_DB.store[sessionId].messages]);
			return;
		}

		
		axios.get(`${API_BASE}/chat/${sessionId}`, { headers: { ...authHeader() } })
		.then(res => {	
			setCurrentChatIndexCallback(index);
			const msgs = res.data.chat?.messages || [];

			const mapped = msgs.flatMap(m => (
				[
					{
						id: m._id,
						role: 'user',
						content: m.query,						
						parent_id: m.parent_id || null,
						children: []
					},
					{
						id: m._id,
						role: 'assistant',
						content: m.answer,
						references: m.references?.length ? getReferences(m.references) : [],
						feedback: (m.feedback !== undefined) ?
							(m.feedback === true ? "liked" : m.feedback === false ? "disliked" : "none")
							: 'user_msg',
						charts: m?.charts,
						report: m?.report
					}
				]
				
			))

			//take out the ones with parent_id and push them to
			// the children array of the message with that id
			// and delete it from mapped

			// iterate from end -> start so splice doesn't skip next elements
			for (let i = mapped.length - 1; i >= 0; i--) {
				const msg = mapped[i];
				if (msg.parent_id) {
					const parent = mapped.find(m => m.id === msg.parent_id);
					if (parent) {
						parent.children.push({
							user_msg: msg,
							assistant_msg: mapped[i + 1]?.role === 'assistant' ? mapped[i + 1] : null
						});
					}
					if (mapped[i + 1]?.role === 'assistant') mapped.splice(i, 2);
					else mapped.splice(i, 1);
				}
			}

			let msgsToShow = [];

			for(let i = 0; i < mapped.length; i+=2) {
				const userMsg = mapped[i].role === 'user' ? mapped[i] : null;
				const assistantMsg = mapped[i + 1]?.role === 'assistant' ? mapped[i + 1] : null;

				if (userMsg?.children.length > 0) {
					//since in the last for loop mapped is iterated from the
					//end to start, the first child in array is the last version of the message
					const firstChild = userMsg.children[0];

					msgsToShow.push(firstChild.user_msg);

					if (firstChild.assistant_msg)
						msgsToShow.push(firstChild.assistant_msg);

				} else {

					if (userMsg)
						msgsToShow.push(userMsg)
					
					if (assistantMsg)
						msgsToShow.push(assistantMsg)
				}

			}

			
			setMessages(msgsToShow)

			// Wait for next animation frame (after render)
			requestAnimationFrame(() => {
				bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
			});
		})
		.catch(err => {
			httpErrorHandler({
				err: err,
				setSnackbar: setSnackbar,
				navigate: navigate
			});
		})

	};

	useEffect(() => {
		const handleTouchStart = (e) => {
			if (e.touches.length === 1) {
				window._touchStartX = e.touches[0].clientX;
				window._touchStartY = e.touches[0].clientY;
			}
		};

		const handleTouchEnd = (e) => {
			if (window._touchStartX !== undefined && e.changedTouches.length === 1) {
				const dx = window._touchStartX - e.changedTouches[0].clientX;
				const dy = Math.abs(window._touchStartY - e.changedTouches[0].clientY);
				if (dx > 50 && dy < 40) setMobileIsOpen(true);
				window._touchStartX = undefined;
				window._touchStartY = undefined;
			}
		};

		document.addEventListener('touchstart', handleTouchStart, { passive: true });
		document.addEventListener('touchend', handleTouchEnd, { passive: true });

		return () => {
			document.removeEventListener('touchstart', handleTouchStart);
			document.removeEventListener('touchend', handleTouchEnd);
		};
	}, []);

	useEffect(() => {
		if (!mobileIsOpen) return;
		function handleClickOutside(e) {
			const sidebar = sidebarRef.current;
			if (sidebar && !sidebar.contains(e.target)) setMobileIsOpen(false);
		}
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [mobileIsOpen]);


	const InProgress = () =>{
		return <>
			<ProgressPanel
				items={progressItems}
				isProcessing={isProcessing}
				error={
					progressError ||
					connectionError ||
					requestError
				}
				isExpanded={isProgressExpanded}
				setIsExpanded={setIsProgressExpanded}
			/>
			<TypingIndicator />
		</>
	}

	return (
		<div className='chatpage-container'>
			<div className={`${sidebarOpen ? '' : 'collapsed'} chat-wrapper`}>
				<div ref={sidebarRef} className={`${mobileIsOpen ? 'open' : ''} logo-sidebar-wrapper`}>
					<ChatPageHeader />
					<Sidebar
						sidebarOpen={sidebarOpen}
						setSidebarOpen={setSidebarOpen}
						chatIds={chatIds}
						currentChatIndex={currentChatIndex}
						handleSelectChat={handleSelectChat}
						handleNewChat={handleNewChat}
						onLogout={onLogout}
						visibleChatCount={visibleChatCount}
						isLoadingMoreChats={isLoadingMoreChats}
						handleSidebarScroll={handleSidebarScroll}
						isTypingChatIndex={isTypingChatIndex}
						user={user}
						firstQueries={firstQueries}
					/>
				</div>

				<div className={`${messages?.length === 0 ? 'welcome-mode' : ''} chat-container`}>					
					<div className="chat-box" onDrop={onDrop} onDragOver={onDragOver}>
						{
							messages?.length === 0 ?
								(<div className='welcome-mode-parent'>
									<div className='welcome-mode-container'>
										<h2>سلام! من زیست‌یار هستم، دستیار هوشمندت در حوزه محیط زیست
											<br />
											هر سوالی درباره موارد ذیل داری، از من بپرس!</h2>
										<div className="tags">
											<span onClick={() => {setSuggestions(legalQuestions); setShowSuggestions(true)}}>#دستیار حقوقی</span>
											<span onClick={() => {setSuggestions(airQualityReports); setShowSuggestions(true)}}>#گزارش کیفیت هوا</span>
											<span onClick={() => {setSuggestions(scenarioQuestions); setShowSuggestions(true)}}>#پیش‌بینی/سناریو</span>
											<span onClick={() => {setSuggestions(webSearchQuestions); setShowSuggestions(true)}}>#سوال از وبسایت سازمان</span>
										</div>
										<InputBox
											input={input}
											setInput={setInput}
											handleSend={handleSend}											
											openFilePicker={openFilePicker}
											fileInputRef={fileInputRef}
											onFilesSelected={onFilesSelected}
											onDrop={onDrop}
											onDragOver={onDragOver}
											isTypingChatIndex={isTypingChatIndex}											
											suggestions={suggestions}
											setSuggestions={setSuggestions}
											showSuggestions={showSuggestions}
											setShowSuggestions={handleSetShowSuggestions}	
											currentChatIndex={currentChatIndex}	
											askWeb={askWeb}
											setAskWeb={setAskWeb}															
										/>
									</div>
									<div className='question-mark' onClick={onQuestionMarkClick}>
										<svg width="16" height="24" viewBox="0 0 16 24" fill="none" xmlns="http://www.w3.org/2000/svg">
											<path d="M7.25108 18.1391C6.77428 18.1391 6.38868 17.7535 6.38868 17.2767C6.38868 16.1487 6.54868 15.1743 6.86708 14.3551C7.10068 13.7375 7.47988 13.1151 8.00148 12.4863C8.38548 12.0287 9.07349 11.3599 10.0687 10.4831C11.0639 9.60475 11.7103 8.90555 12.0095 8.38395C12.3087 7.86235 12.4559 7.29275 12.4559 6.67515C12.4559 5.55675 12.0191 4.57595 11.1471 3.72955C10.2751 2.88315 9.20468 2.46075 7.93748 2.46075C6.71348 2.46075 5.69108 2.84475 4.87188 3.61115C4.27348 4.17115 3.82388 4.96155 3.52628 5.98235C3.32148 6.68315 2.63828 7.13115 1.91348 7.04475C0.995084 6.93595 0.390284 6.02715 0.652684 5.13915C1.05588 3.77435 1.73108 2.67515 2.67668 1.83995C3.99028 0.676747 5.72788 0.0959473 7.88948 0.0959473C10.1775 0.0959473 12.0031 0.718347 13.3663 1.96475C14.7295 3.21115 15.4095 4.71675 15.4095 6.48315C15.4095 7.50555 15.1695 8.44795 14.6911 9.30875C14.2127 10.1711 13.2751 11.2191 11.8815 12.4543C10.9455 13.2847 10.3327 13.8975 10.0447 14.2911C9.75669 14.6847 9.54388 15.1375 9.40628 15.6479C9.30388 16.0271 9.23348 16.5839 9.19508 17.3151C9.17108 17.7759 8.78868 18.1375 8.32788 18.1375H7.25108V18.1391ZM6.22868 22.2671C6.22868 21.3631 6.96148 20.6303 7.86548 20.6303C8.76948 20.6303 9.50229 21.3631 9.50229 22.2671C9.50229 23.1711 8.76948 23.9039 7.86548 23.9039C6.96148 23.9039 6.22868 23.1711 6.22868 22.2671Z" fill="#06923E" />
										</svg>
									</div>
								</div>
								) : ''
						}
						{ messages?.map((msg, i) => (
							<>
								<Message
									key={i}
									msg={msg}
									index={i}
									onFeedback={handleFeedback}
									exportMenuOpen={exportMenuOpen}
									onToggleExportMenu={toggleExportMenu}
									exportMenuStyle={exportMenuStyle}
									messages={messages}
									chatIds={chatIds}
									currentChatIndex={currentChatIndex}
									headerRef={chatHeaderRef}
									onEdit={setEditingIndexCallback}
									isEditing={editingIndex === i}
									editingText={editingText}
									setEditingText={setEditingText}
									saveEdit={saveEdit}
									editingIndex={editingIndex}
								/>
								{ savingEditIndex === i && isTypingChatIndex === currentChatIndex && <InProgress /> }
							</>
						))}
						{/* Bottom typing indicator only when not showing inline saving indicator */}
						{!Number.isInteger(savingEditIndex) && isTypingChatIndex === currentChatIndex && <InProgress />}
						<div ref={bottomRef} />
					</div>

					<AttachmentsBar attachments={attachments} removeAttachment={removeAttachment} />

					{
						messages.length !== 0 ?
							(<InputBox
								input={input}
								setInput={setInput}
								handleSend={handleSend}								
								openFilePicker={openFilePicker}
								fileInputRef={fileInputRef}
								onFilesSelected={onFilesSelected}
								onDrop={onDrop}
								onDragOver={onDragOver}
								suggestions={suggestions}
								setSuggestions={setSuggestions}
								showSuggestions={showSuggestions}
								setShowSuggestions={handleSetShowSuggestions}
								isTypingChatIndex={isTypingChatIndex}
								currentChatIndex={currentChatIndex}
								askWeb={askWeb}
								setAskWeb={setAskWeb}
							/>) : ''
					}

				</div>
			</div>
			<div className="powered-by">
				Powered by: ICT-TMU
			</div>
			{snackbar.message && (
				<Snackbar
					message={snackbar.message}
					type={snackbar.type}
					duration={3000}
					onClose={() => setSnackbar({ message: "", type: "" })}
				/>
			)}
		</div>
	);
};

export default ChatUI;